import React, { useState, useEffect } from 'react';
import { supportApi } from '../../services/supportApi';
import { mqttSupportService } from '../../services/mqttSupportService';
import './SupportRequestList.css';

interface AIMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string;
}

interface SupportRequest {
  id: number;
  requesterId: number;
  requesterName: string;
  requesterPhone: string;
  requesterEmail: string;
  subject: string;
  description: string;
  category: string;
  priority: string;
  status: string;
  requestedAt: string;
  aiConversationHistory?: AIMessage[];
  hasAiHistory: boolean;
  chatRoomRoomId?: string;
  assignedAdminId?: number;
  assignedAdminName?: string;
}

interface SupportRequestListProps {
  onOpenChat: (request: SupportRequest) => void;
}

const SupportRequestList: React.FC<SupportRequestListProps> = ({ onOpenChat }) => {
  const [requests, setRequests] = useState<SupportRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'pending' | 'active' | 'my-assigned' | 'my-chats'>('pending');
  const [error, setError] = useState<string>('');
  const [mqttConnected, setMqttConnected] = useState(false);
  const [chatRooms, setChatRooms] = useState<any[]>([]);
  const newRequestCallbackRef = React.useRef<((notification: any) => void) | null>(null);
  const statusUpdateCallbackRef = React.useRef<((notification: any) => void) | null>(null);

  useEffect(() => {
    loadRequests();
    connectToMQTT();

    return () => {
      if (newRequestCallbackRef.current) {
        mqttSupportService.offNewSupportRequest(newRequestCallbackRef.current);
      }
      if (statusUpdateCallbackRef.current) {
        mqttSupportService.offStatusUpdate(statusUpdateCallbackRef.current);
      }
    };
  }, [filter]);

  const connectToMQTT = () => {
    const token = localStorage.getItem('adminToken');

    console.log('🔌 [Support List] Connecting to MQTT...');

    mqttSupportService.connect(token || undefined);

    // Wait a bit for connection, then register callbacks
    setTimeout(() => {
      const isConnected = mqttSupportService.isConnectedToBroker();
      console.log('📡 [Support List] MQTT connection status:', isConnected ? 'Connected ✅' : 'Disconnected ❌');
      setMqttConnected(isConnected);

      // Register callback for new support requests
      newRequestCallbackRef.current = (notification) => {
        console.log('🆘 [Support List] New support request notification:', notification);
        // Reload requests if we're on pending tab
        if (filter === 'pending') {
          loadRequests();
        }
      };
      mqttSupportService.onNewSupportRequest(newRequestCallbackRef.current);

      // Register callback for status updates
      statusUpdateCallbackRef.current = (notification) => {
        console.log('📊 [Support List] Status update notification:', notification);
        // Reload requests
        loadRequests();
      };
      mqttSupportService.onStatusUpdate(statusUpdateCallbackRef.current);
    }, 2000);

    // Check connection status periodically
    const checkConnection = setInterval(() => {
      const connected = mqttSupportService.isConnectedToBroker();
      setMqttConnected(connected);
    }, 3000);

    return () => clearInterval(checkConnection);
  };

  const loadRequests = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('adminToken');
      if (!token) {
        setError('No authentication token found');
        return;
      }

      if (filter === 'my-chats') {
        // Load from chat rooms endpoint - shows all active support chats
        const rooms = await supportApi.getMyChatRooms(token);
        setChatRooms(rooms);
        setRequests([]);
      } else {
        // Load from support requests endpoint
        let data: SupportRequest[];
        if (filter === 'pending') {
          data = await supportApi.getPendingRequests(token);
        } else if (filter === 'active') {
          data = await supportApi.getActiveRequests(token);
        } else {
          data = await supportApi.getMyAssigned(token);
        }
        setRequests(data);
        setChatRooms([]);
      }
    } catch (error: any) {
      console.error('Failed to load requests:', error);
      setError(error.message || 'Failed to load support requests');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (requestId: number) => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        alert('No authentication token found');
        return;
      }

      const result = await supportApi.acceptRequest(token, requestId);
      alert('Request accepted! Chat room created.');

      // Reload requests
      await loadRequests();

      // Open chat if room was created
      if (result.chatRoomRoomId) {
        const request = requests.find(r => r.id === requestId);
        if (request) {
          onOpenChat({ ...request, chatRoomRoomId: result.chatRoomRoomId });
        }
      }
    } catch (error: any) {
      console.error('Failed to accept request:', error);
      alert(error.message || 'Failed to accept request');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toUpperCase()) {
      case 'PENDING':
        return '#ffc107';
      case 'ACTIVE':
        return '#28a745';
      case 'RESOLVED':
        return '#6c757d';
      default:
        return '#999';
    }
  };

  if (loading) {
    return (
      <div className="support-requests-loading">
        <div className="spinner"></div>
        <p>Loading support requests...</p>
      </div>
    );
  }

  return (
    <div className="support-requests">
      <div className="support-requests-header">
        <h2>Support Requests</h2>
        <div className={`mqtt-status-indicator ${mqttConnected ? 'online' : 'offline'}`}>
          <span className="status-dot"></span>
          {mqttConnected ? 'Online - Real-time updates active' : 'Offline - Connecting...'}
        </div>
      </div>

      {error && (
        <div className="error-message">
          <p>{error}</p>
          <button onClick={loadRequests}>Retry</button>
        </div>
      )}

      <div className="filters">
        <button
          className={filter === 'pending' ? 'filter-button active' : 'filter-button'}
          onClick={() => setFilter('pending')}
        >
          Pending ({requests.filter(r => r.status === 'PENDING').length})
        </button>
        <button
          className={filter === 'active' ? 'filter-button active' : 'filter-button'}
          onClick={() => setFilter('active')}
        >
          Active
        </button>
        <button
          className={filter === 'my-assigned' ? 'filter-button active' : 'filter-button'}
          onClick={() => setFilter('my-assigned')}
        >
          My Assigned
        </button>
        <button
          className={filter === 'my-chats' ? 'filter-button active' : 'filter-button'}
          onClick={() => setFilter('my-chats')}
        >
          My Chats ({chatRooms.length})
        </button>
      </div>

      <div className="requests-list">
        {/* Show chat rooms if in "my-chats" filter */}
        {filter === 'my-chats' && chatRooms.length > 0 && (
          chatRooms.map((room) => (
            <div key={room.id} className="request-card chat-room-card">
              <div className="chat-row-main">
                <div className="chat-user-info">
                  <h3>{room.otherUserName || 'Support Chat'}</h3>
                  <div className="chat-meta">
                    <span className="chat-role">{room.otherUserRole}</span>
                    {room.lastMessage && (
                      <span className="chat-preview">{room.lastMessage.substring(0, 60)}...</span>
                    )}
                  </div>
                </div>
                <div className="chat-time-info">
                  <div className="chat-time">{new Date(room.lastMessageAt).toLocaleDateString()}</div>
                  <div className="chat-time-small">{new Date(room.lastMessageAt).toLocaleTimeString()}</div>
                </div>
                <div className="chat-badges">
                  {room.unreadCount > 0 && (
                    <span className="unread-badge">
                      {room.unreadCount}
                    </span>
                  )}
                </div>
                <div className="request-actions">
                  <button
                    className="btn-chat"
                    onClick={() => {
                      // Create a fake request object to open chat
                      const fakeRequest: SupportRequest = {
                        id: room.id,
                        requesterId: room.otherUserId,
                        requesterName: room.otherUserName,
                        requesterPhone: 'N/A',
                        requesterEmail: 'N/A',
                        subject: `Chat with ${room.otherUserName}`,
                        description: '',
                        category: 'SUPPORT',
                        priority: 'MEDIUM',
                        status: 'ACTIVE',
                        requestedAt: room.createdAt,
                        hasAiHistory: false,
                        chatRoomRoomId: room.roomId,
                      };
                      onOpenChat(fakeRequest);
                    }}
                  >
                    Open
                  </button>
                </div>
              </div>
            </div>
          ))
        )}

        {/* Show support requests for other filters */}
        {filter !== 'my-chats' && requests.length === 0 && (
          <div className="no-requests">
            <p>No {filter} support requests</p>
          </div>
        )}

        {filter !== 'my-chats' && requests.length > 0 && (
          requests.map((request) => (
            <div key={request.id} className="request-card">
              <div className="chat-row-main">
                <div className="chat-user-info">
                  <h3>{request.requesterName}</h3>
                  <div className="chat-meta">
                    <span className="chat-phone">{request.requesterPhone}</span>
                    <span className="status-badge">
                      {request.status}
                    </span>
                  </div>
                </div>
                <div className="request-actions">
                  {request.status === 'PENDING' && (
                    <button
                      className="btn-accept"
                      onClick={() => handleAccept(request.id)}
                    >
                      Accept
                    </button>
                  )}
                  {request.chatRoomRoomId && (
                    <button
                      className="btn-chat"
                      onClick={() => onOpenChat(request)}
                    >
                      Open Chat
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}

        {/* Show empty state for my-chats filter */}
        {filter === 'my-chats' && chatRooms.length === 0 && (
          <div className="no-requests">
            <p>No active support chats</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupportRequestList;

